import axios from 'axios'
import { supabase } from './supabase'
import { useAuthStore } from '../store/authStore'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    // Use cached token from store — avoids async getSession() on every request
    let token = useAuthStore.getState().token

    // Fallback: if store has no token yet (e.g. during bootstrap), check Supabase
    if (!token) {
      const { data: { session } } = await supabase.auth.getSession()
      token = session?.access_token ?? null
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config
    if (error.response?.status === 401 && !config._retried) {
      config._retried = true
      // Token expired — refresh and retry once
      const { data: { session }, error: refreshError } = await supabase.auth.refreshSession()
      if (refreshError || !session) {
        // Refresh failed — let the auth listener handle sign-out
        // Don't hard-navigate, it kills app state
        useAuthStore.getState().logout()
        return Promise.reject(error)
      }
      // Update store with refreshed token
      const store = useAuthStore.getState()
      store.setAuth(session.access_token, store.player)
      // Retry original request with new token
      config.headers.Authorization = `Bearer ${session.access_token}`
      return api.request(config)
    }
    return Promise.reject(error)
  }
)
